import {useHookClass} from "@dvrt/graphql-widgets";
import Loading from "../breadcrumbs/Loading";
import {RecordList} from "@/components/RecordList";
import {fieldContentGenerators} from "@/components/call-list/CallListPage";
import DataSource from "./dataSource";
import {PersonalExtensionCallListQuery} from "./queries.generated";
import VoicemailButton from "../call-list/VoicemailButton";
import {useStructureConfig} from "@/context/StructureConfigProvider";
import {defineMessage} from "react-intl";

type Props = {
    extensionId: number;
};

type Call = PersonalExtensionCallListQuery["callList"]["calls"][number];

export default ({extensionId}: Props) => {
    const config = useStructureConfig();
    const voicemailEnabled = config.isEnabledService("voicemail");

    const dataSource = useHookClass(DataSource, extensionId, voicemailEnabled);

    if (!dataSource.loaded) return <Loading />;

    return (
        <div>
            <RecordList
                records={dataSource.callListData.calls}
                loading={false}
                fields={[
                    {
                        name: "remoteNumber",
                        label: defineMessage({id: "call_list.remoteNumber", defaultMessage: "Remote number"}),
                    },
                    {
                        name: "remoteE164",
                        label: defineMessage({id: "call_list.remoteE164", defaultMessage: "Remote E.164"}),
                    },
                    {
                        name: "direction",
                        label: defineMessage({id: "call_list.direction", defaultMessage: "Direction"}),
                    },
                    {
                        name: "segmentType",
                        label: defineMessage({id: "call_list.segmentType", defaultMessage: "Segment type"}),
                    },
                    {
                        name: "callTime",
                        label: defineMessage({id: "call_list.callTime", defaultMessage: "Time"}),
                    },
                    {
                        name: "duration",
                        label: defineMessage({id: "call_list.duration", defaultMessage: "Duration"}),
                    },
                ]}
                generators={fieldContentGenerators as Record<keyof Call, (call: Call) => any>}
                initialOrder={["~callTime"]}
                actions={
                    voicemailEnabled
                        ? [
                              {
                                  header: {
                                      name: "voicemail",
                                      label: defineMessage({id: "call_list.voicemail", defaultMessage: "Voicemail"}),
                                  },
                                  renderer: (record: Call) => {
                                      const vm = dataSource.voicemailsData?.voicemails?.find(
                                          (vm) => vm.callId === record.callId,
                                      );
                                      if (!vm) return null;

                                      return (
                                          <VoicemailButton
                                              voicemailId={vm.voicemailId}
                                              extensionId={extensionId}
                                              listened={vm.listened}
                                          />
                                      );
                                  },
                                  orderer: (isDescending: boolean) => (a: any, b: any) => {
                                      const aVm = dataSource.voicemailsData?.voicemails?.find(
                                          (vm) => vm.callId === a.callId,
                                      );
                                      const bVm = dataSource.voicemailsData?.voicemails?.find(
                                          (vm) => vm.callId === b.callId,
                                      );

                                      if (!aVm && !bVm) return 0;
                                      if (!aVm) return isDescending ? 1 : -1;
                                      if (!bVm) return isDescending ? -1 : 1;

                                      if (aVm.listened === bVm.listened) return 0;
                                      if (aVm.listened) return isDescending ? 1 : -1;
                                      return isDescending ? -1 : 1;
                                  },
                              },
                          ]
                        : []
                }
            />
        </div>
    );
};
